import moment from "moment-timezone";
import { IFlightPairingStatus } from "@vmosolutions/oc-interfaces";
import { ITableColumn } from "src/components/FilterSearchTable/TableComponents/TableHeader";

export interface IFlightPairingStatusMap {
  [key: string]: IFlightPairingStatus[]; // 123|CYYC|KEWR: []
}

export interface IFlightPairingStatusRow {
  id: string; // format: flightNumber|origin|destination (should result in each row having a unique id)
  flightNumber: string;
  origin: string;
  destination: string;
  [key: number]: IFlightPairingStatus[]; // Day of month containing assigned flights.
}
interface IMonthDayCount {
  dayCount: number;
  isFullMonth: boolean;
}

/**
 * Accepts start and end times in epoch values and returns an object containing the
 * total day count between both values as well as a boolen indicating if the total matches the month day count of the start value.
 * @param start -
 * @param end -
 */
export const returnMonthDayCount = (start: number, end: number): IMonthDayCount => {
  // Calculate the number of days needed to display in the selected range.
  // Note: Second added to end time to ensure it counts all selected days
  const momentStart = moment.utc(start);
  const momentEnd = moment.utc(end).add(1, "second");
  const dayCount = momentEnd.diff(momentStart, "days");
  const isFullMonth =
    momentEnd.diff(start, "month") === 1 && moment(start).utc().format("MM") === moment(end).utc().format("MM");
  return { dayCount, isFullMonth };
};

export const pairingStatusDesc = {
  tableTitle: "",
  tableDescription: "", //"Flight Pairing Status",
  noMessage: "No flight pairing statuses found",
};

export const returnPairingStatusInfo = (start: number, end: number): any => {
  const columns: ITableColumn[] = [
    {
      id: "flightNumber",
      value: "flightNumber",
      index: 1,
      numeric: true,
      label: "Flight",
      type: "number",
      width: 10,
      visible: true,
      filter: false,
    },
    {
      id: "origin",
      value: "origin",
      index: 2,
      numeric: true,
      label: "Orig.",
      type: "string",
      width: 10,
      visible: true,
      filter: false,
    },
    {
      id: "destination",
      value: "destination",
      index: 3,
      numeric: true,
      label: "Dest.",
      type: "string",
      width: 10,
      visible: true,
      filter: false,
    },
  ];

  const { dayCount } = returnMonthDayCount(start, end);

  // Define columns for everyone available day in the month.
  for (let i = 0; i < dayCount; i++) {
    const columnId = moment.utc(start).add(i, "days").format("YYYY-MM-DD");
    columns.push({
      id: columnId,
      value: columnId,
      index: i + 4, // Add 3 to account for the 3 column above
      numeric: true,
      // Display only the date portion of the associated column.
      // Trim the leading zeros if present for a cleaner look.
      label: parseInt(columnId.substr(-2), 10).toString(),
      type: "string",
      width: 10,
      visible: true,
      filter: false,
      sort: false, // Ensure date columns can't be sorted
    });
  }
  return columns;
};

// Parse the flight pairing status data and return a format compatible with the search filter table.
export const returnPairingStatusMap = (data: IFlightPairingStatus[]): IFlightPairingStatusMap => {
  if (!data || data.length === 0) return null;
  // Use moment to determine how many days
  const tableFlightObject = data.reduce((acc: { [key: string]: any }, currentValue) => {
    // All row content must have matching flight numbers, origin and destinations.
    const key = `${currentValue.flightNumber}|${currentValue.origin}|${currentValue.destination}`;
    const date = moment(currentValue.actualOut).utc().format("YYYY-MM-DD");
    if (acc[key]) {
      if (acc[key][date]) {
        // console.log("this row's date contains more than 1 flight");
        acc[key][date].push(currentValue);
      } else {
        acc[key][date] = [currentValue];
      }
    } else {
      acc[key] = { [date]: [currentValue] };
    }
    return acc;
  }, {});
  return tableFlightObject;
};

export const returnPairingStatusRows = (
  map: IFlightPairingStatusMap,
  start: number,
  end: number
): IFlightPairingStatusRow[] => {
  const tableRows: IFlightPairingStatusRow[] = [];

  for (const [key, value] of Object.entries(map)) {
    const [flightNumber, origin, destination] = key.split("|");
    const tableRow: IFlightPairingStatusRow = {
      id: key,
      flightNumber,
      origin,
      destination,
    };
    const { dayCount } = returnMonthDayCount(start, end);
    for (let i = 0; i < dayCount; i++) {
      const dayKey = moment.utc(start).add(i, "days").format("YYYY-MM-DD") as keyof typeof value;
      (tableRow as any)[dayKey] = value[dayKey] ? value[dayKey] : [];
    }
    tableRows.push(tableRow);
  }

  return tableRows;
};
